import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_syndic-app-1/artifacts/1ku8mjse_Logo%20Syndicappli_White_2022.png";
const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token manquant. Veuillez refaire une demande de réinitialisation.');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.post(`${API}/api/auth/verify-reset-token`, { token });
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || 'Token invalide ou expiré');
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, { 
        token, 
        password 
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00A88D 100%)' }}
      >
        <div className="text-center">
          <Loader2 className="animate-spin text-white mx-auto mb-4" size={40} />
          <p className="text-white/90">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #00D3A8 0%, #00A88D 100%)' }}
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-5xl flex flex-col items-center">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={LOGO_URL} 
            alt="SyndicAppli" 
            className="h-12 w-auto mx-auto"
          />
          <p className="text-white/80 text-sm mt-2 font-medium">La Vie de votre Copro</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm" data-testid="reset-password-card">
          {success ? (
            // Success State
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-500" size={32} />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-3">Mot de passe modifié !</h2>
              <p className="text-slate-600 text-sm mb-6">
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
              </p>

              <Link 
                to="/login"
                className="w-full inline-block text-center text-white font-medium py-3 px-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:opacity-95"
                style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
                data-testid="go-to-login-btn"
              >
                Se connecter
              </Link>
            </div>
          ) : !tokenValid ? (
            // Invalid Token State
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="text-red-500" size={32} />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-3">Lien invalide</h2>
              <p className="text-slate-600 text-sm mb-6">
                {error || 'Ce lien de réinitialisation est invalide ou a expiré.'}
              </p>

              <Link 
                to="/forgot-password"
                className="w-full inline-block text-center text-white font-medium py-3 px-4 rounded-full transition-all shadow-lg hover:shadow-xl hover:opacity-95 mb-4"
                style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
              >
                Faire une nouvelle demande
              </Link>

              <Link 
                to="/login"
                className="text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            // Form State
            <>
              <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">
                Nouveau mot de passe
              </h2>
              <p className="text-slate-500 text-sm text-center mb-6">
                Choisissez un nouveau mot de passe pour votre compte{' '}
                <span className="font-medium text-slate-700">{userEmail}</span>
              </p>

              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2" htmlFor="password">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 focus:border-[#00D3A8] transition-all placeholder:text-slate-400"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Minimum 6 caractères</p>
                </div>

                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-2" htmlFor="confirmPassword">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 focus:border-[#00D3A8] transition-all placeholder:text-slate-400"
                      placeholder="••••••••"
                      required
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-medium py-3 px-4 rounded-full transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:opacity-95"
                  style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Réinitialisation...</span>
                    </>
                  ) : (
                    <span>Réinitialiser le mot de passe</span>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-white/50 text-xs mt-8">
          © 2026 SyndicAppli - Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
